import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import isEqual from 'lodash-es/isEqual';

import { NewMatchDailyFrequency } from '../../interfaces/enums/new-match-daily-frequency.enum';
import { NewMatchMonthlyFrequency } from '../../interfaces/enums/new-match-monthly-frequency.enum';
import { NewMatchWeekDays } from '../../interfaces/enums/new-match-weekdays.enum';
import { NewMatchWeeklyFrequency } from '../../interfaces/enums/new-match-weekly-frequency.enum';
import { NewMatchNotificationFrequency, NewMatchNotificationFrequencyKind } from '../../interfaces/new-match-settings/new-match-notification-frequency';
import { NewMatchSettingsNotificationFrequencyOption } from '../../interfaces/new-match-settings/new-match-notification-frequency-option';
import { NewMatchSettingsFormModel } from '../../interfaces/new-match-settings/new-match-settings-form-model';
import { NewMatchSettingsModel } from '../../interfaces/new-match-settings/new-match-settings.model';
import { NewMatchSettingsWeekDayOption } from '../../interfaces/new-match-settings/new-match-week-day-option';
import { AppearanceType, Color } from '@core-controls/constants/core.constants';
import { RpcButtonComponent } from '../../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { RpcSelectComponent } from '../../../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'new-match-settings',
    templateUrl: './new-match-settings.component.html',
    styleUrls: ['./new-match-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, RpcButtonComponent, RpcSelectComponent, TranslateModule]
})
export class NewMatchSettingsComponent implements OnChanges, OnDestroy {
    @Input() public settings: NewMatchSettingsModel;

    @Output() public settingsChanged = new EventEmitter<NewMatchSettingsModel>();

    public newMatchSettingsForm: UntypedFormGroup;
    public frequencyPerDay: RpcSelectControltOptions;
    public weeklyFrequency: RpcSelectControltOptions;
    public weeklyFrequencyDay: RpcSelectControltOptions;
    public monthlyFrequency: RpcSelectControltOptions;

    public readonly NewMatchWeekDays = NewMatchWeekDays;

    public readonly notificationFrequencyOptions: readonly NewMatchSettingsNotificationFrequencyOption[] = [
        { titleTranslationKey: 'NM.TITLES.REALTIME', frequency: 'realtime' },
        { titleTranslationKey: 'NM.TITLES.DAILY', frequency: 'daily' },
        { titleTranslationKey: 'NM.TITLES.WEEKLY', frequency: 'weekly' },
        { titleTranslationKey: 'NM.TITLES.MONTHLY', frequency: 'monthly' }
    ];

    public readonly weekDayOptions: readonly NewMatchSettingsWeekDayOption[] = [
        { titleTranslationKey: 'NM.DAYS.SUN', day: NewMatchWeekDays.Sunday },
        { titleTranslationKey: 'NM.DAYS.MON', day: NewMatchWeekDays.Monday },
        { titleTranslationKey: 'NM.DAYS.TUE', day: NewMatchWeekDays.Tuesday },
        { titleTranslationKey: 'NM.DAYS.WED', day: NewMatchWeekDays.Wednesday },
        { titleTranslationKey: 'NM.DAYS.THU', day: NewMatchWeekDays.Thursday },
        { titleTranslationKey: 'NM.DAYS.FRI', day: NewMatchWeekDays.Friday },
        { titleTranslationKey: 'NM.DAYS.SAT', day: NewMatchWeekDays.Saturday }
    ];

    private unsubscribe$ = new Subject<void>();
    private readonly updateDebounceTime = 200;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) { }

    public ngOnChanges(changes: SimpleChanges<NewMatchSettingsComponent>): void {
        if (changes?.settings?.currentValue != null) {
            this.unsubscribe();
            this.unsubscribe$ = new Subject<void>();

            this.buildForm(changes.settings.currentValue);
            const currentSettings = changes.settings.currentValue;

            this.newMatchSettingsForm.valueChanges
                .pipe(debounceTime(this.updateDebounceTime), takeUntil(this.unsubscribe$))
                .subscribe((settings: NewMatchSettingsFormModel) => {
                    const updatedSettings = this.createSettingsModel(settings);

                    if (updatedSettings != null && !isEqual(currentSettings, updatedSettings)) {
                        this.settingsChanged.next(updatedSettings);
                    }
                });
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe();
    }

    public toggleEmailNotificationFrequency(kind: NewMatchNotificationFrequencyKind): void {
        this.newMatchSettingsForm.controls.notificationFrequency.setValue(kind);
        this.changeDetectorRef.detectChanges();
    }

    public getEmailNotificationFrequencyButtonColor(kind: NewMatchNotificationFrequencyKind): Color | null {
        return this.isEmailNotificationFrequencySelected(kind) ? 'accent' : null;
    }

    public getEmailNotificationFrequencyButtonAppearance(kind: NewMatchNotificationFrequencyKind): AppearanceType {
        return this.isEmailNotificationFrequencySelected(kind) ? 'flat' : 'stroke';
    }

    public isEmailNotificationFrequencySelected(kind: NewMatchNotificationFrequencyKind): boolean {
        return this.newMatchSettingsForm.controls.notificationFrequency.value === kind;
    }

    public getDailyFrequencyButtonColor(weekDay: NewMatchWeekDays): Color | null {
        const frequency = this.newMatchSettingsForm.controls.notificationFrequency.value as string;

        if (frequency === 'daily') {
            const days = this.newMatchSettingsForm.controls.selectedDays.value as NewMatchWeekDays[];

            return days.includes(weekDay) ? 'accent' : null;
        }

        return null;
    }

    public getDailyFrequencyButtonAppearance(weekDay: NewMatchWeekDays): AppearanceType {
        const days: NewMatchWeekDays[] = this.newMatchSettingsForm.controls.selectedDays.value;

        return days.includes(weekDay) ? 'flat' : 'stroke';
    }

    public toggleDailyFrequencyDay(weekDay: NewMatchWeekDays): void {
        const frequency = this.newMatchSettingsForm.controls.notificationFrequency.value as string;

        if (frequency === 'daily') {
            const days = this.newMatchSettingsForm.controls.selectedDays.value as NewMatchWeekDays[];
            const index = days.indexOf(weekDay);

            if (index > -1) {
                days.splice(index, 1);
            } else {
                days.push(weekDay);
            }

            this.newMatchSettingsForm.controls.selectedDays.setValue(days);
        }
    }

    private createSettingsModel(settings: NewMatchSettingsFormModel): NewMatchSettingsModel {
        let notificationFrequency: NewMatchNotificationFrequency;

        switch (settings.notificationFrequency) {
            case 'realtime':
                notificationFrequency = { kind: 'realtime' };
                break;
            case 'daily':
                if (settings.selectedDays.length === 0) {
                    return null;
                }

                notificationFrequency = { kind: 'daily', frequency: settings.frequencyPerDay, days: settings.selectedDays };
                break;
            case 'weekly':
                notificationFrequency = { kind: 'weekly', frequency: settings.weeklyFrequency, day: settings.weeklyFrequencyDay };
                break;
            case 'monthly':
                notificationFrequency = { kind: 'monthly', frequency: settings.monthlyFrequency };
                break;
        }

        return new NewMatchSettingsModel(notificationFrequency);
    }

    private buildForm(settings: NewMatchSettingsModel): void {
        if (this.newMatchSettingsForm == null) {
            this.createForm(settings);
        }

        switch (settings.emailNotificationFrequency.kind) {
            case 'daily':
                this.frequencyPerDay.selectedValue = settings.emailNotificationFrequency.frequency;
                this.newMatchSettingsForm.controls.frequencyPerDay.setValue(settings.emailNotificationFrequency.frequency);
                this.newMatchSettingsForm.controls.selectedDays.setValue(settings.emailNotificationFrequency.days.slice());
                break;
            case 'weekly':
                this.weeklyFrequency.selectedValue = settings.emailNotificationFrequency.frequency;
                this.weeklyFrequencyDay.selectedValue = settings.emailNotificationFrequency.day;
                this.newMatchSettingsForm.controls.weeklyFrequency.setValue(settings.emailNotificationFrequency.frequency);
                this.newMatchSettingsForm.controls.weeklyFrequencyDay.setValue(settings.emailNotificationFrequency.day);
                break;
            case 'monthly':
                this.monthlyFrequency.selectedValue = settings.emailNotificationFrequency.frequency;
                this.newMatchSettingsForm.controls.monthlyFrequency.setValue(settings.emailNotificationFrequency.frequency);
                break;
            case 'realtime':
            default:
                break;
        }
    }

    private createForm(settings: NewMatchSettingsModel) {
        this.newMatchSettingsForm = this.formBuilder.group({
            notificationFrequency: [settings.emailNotificationFrequency.kind],
            selectedDays: [[...Object.values(NewMatchWeekDays).filter(val => !isNaN(Number(val)))]],
            frequencyPerDay: [NewMatchDailyFrequency.FourTimesPerDay],
            weeklyFrequency: [NewMatchWeeklyFrequency.EveryOneWeek],
            weeklyFrequencyDay: [NewMatchWeekDays.Sunday],
            monthlyFrequency: [NewMatchMonthlyFrequency.Every1st]
        });

        this.frequencyPerDay = {
            formGroup: this.newMatchSettingsForm,
            controlName: 'frequencyPerDay',
            options: [
                { value: NewMatchDailyFrequency.OncePerDay, text: 'Once' },
                { value: NewMatchDailyFrequency.TwoTimesPerDay, text: 'Two Times' },
                { value: NewMatchDailyFrequency.FourTimesPerDay, text: 'Four Times' },
            ],
            selectedValue: NewMatchDailyFrequency.FourTimesPerDay
        };

        this.weeklyFrequency = {
            formGroup: this.newMatchSettingsForm,
            controlName: 'weeklyFrequency',
            options: [
                { value: NewMatchWeeklyFrequency.EveryOneWeek, text: '1' },
                { value: NewMatchWeeklyFrequency.EveryTwoWeeks, text: '2' },
                { value: NewMatchWeeklyFrequency.EveryThreeWeeks, text: '3' },
                { value: NewMatchWeeklyFrequency.EveryFourWeeks, text: '4' },
            ],
            selectedValue: NewMatchWeeklyFrequency.EveryOneWeek
        };

        this.weeklyFrequencyDay = {
            formGroup: this.newMatchSettingsForm,
            controlName: 'weeklyFrequencyDay',
            options: [
                { value: NewMatchWeekDays.Sunday, text: 'Sunday' },
                { value: NewMatchWeekDays.Monday, text: 'Monday' },
                { value: NewMatchWeekDays.Tuesday, text: 'Tuesday' },
                { value: NewMatchWeekDays.Wednesday, text: 'Wednesday' },
                { value: NewMatchWeekDays.Thursday, text: 'Thursday' },
                { value: NewMatchWeekDays.Friday, text: 'Friday' },
                { value: NewMatchWeekDays.Saturday, text: 'Saturday' },
            ],
            selectedValue: NewMatchWeekDays.Sunday
        };

        this.monthlyFrequency = {
            formGroup: this.newMatchSettingsForm,
            controlName: 'monthlyFrequency',
            options: Object
                .keys(NewMatchMonthlyFrequency)
                .filter(key => isNaN(Number(key)))
                .map(key => ({ value: NewMatchMonthlyFrequency[key], text: key.substring('Every'.length) })),
            selectedValue: NewMatchMonthlyFrequency.Every1st
        };
    }

    private unsubscribe(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}